import React, { useState, useEffect } from 'react';
import { Icons } from './ui/Icons';
import { AppHeader } from './ui/AppHeader';
import { UserProfile, Organization } from '../types';
import { createOrganization, joinOrganization, getUserProfile, searchOrganizations } from '../services/firebase';

interface OnboardingProps {
  userId: string;
  userName: string;
  user: UserProfile;
  onComplete: () => void;
  onLogout: () => void;
  onNavigate?: (tab: string) => void;
}

export const Onboarding: React.FC<OnboardingProps> = ({ userId, userName, user, onComplete, onLogout, onNavigate }) => {
  const [activeTab, setActiveTab] = useState('browse');

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    if (onNavigate) {
      onNavigate(tab);
    }
  };
  const [mode, setMode] = useState<'select' | 'join' | 'create'>('join');
  const [inputCode, setInputCode] = useState('');
  const [orgName, setOrgName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isAutoJoining, setIsAutoJoining] = useState(false);
  const [isTeacher, setIsTeacher] = useState(false);
  const [success, setSuccess] = useState(false);

  // Ensure loading state if user has orgId
  useEffect(() => {
    if (user.orgId) {
      setSuccess(true);
    }
  }, [user.orgId]);

  // Search State
  const [joinMethod, setJoinMethod] = useState<'code' | 'search'>('search');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Organization[]>([]);
  const [searching, setSearching] = useState(false);

  // Check for account type preference and auto-select mode
  useEffect(() => {
    const accountType = sessionStorage.getItem('accountType');
    if (accountType === 'teacher') {
      setIsTeacher(true);
      setMode('create');
    } else {
      // For students or when no accountType is set, always use join mode
      setIsTeacher(false);
      setMode('join');
    }
    // Always clear the storage after reading
    sessionStorage.removeItem('accountType');
  }, []);

  useEffect(() => {
    const orgCode = localStorage.getItem('orgCode');
    if (orgCode) {
      setIsAutoJoining(true);
      setLoading(true);
      joinOrganization(userId, orgCode)
        .then(org => {
          if (org) {
            localStorage.removeItem('orgCode');
            setSuccess(true);
            // onComplete(); // Wait for App listener
            return;
          }
          setError("Organization not found. Please check your code.");
          setLoading(false);
          setIsAutoJoining(false);
          localStorage.removeItem('orgCode');
        })
        .catch(() => {
          setError("Failed to join organization.");
          setLoading(false);
          setIsAutoJoining(false);
          localStorage.removeItem('orgCode');
        });
    }
  }, [userId, onComplete]);

  // Debounced Search
  useEffect(() => {
    // If empty search, fetch initial list (will only return if < 10 total)
    if (joinMethod === 'search' && searchQuery.length === 0) {
      const fetchInitial = async () => {
        setSearching(true);
        try {
          const results = await searchOrganizations('');
          setSearchResults(results);
        } catch (e) {
          console.error(e);
        } finally {
          setSearching(false);
        }
      };
      fetchInitial();
      return;
    }

    if (joinMethod === 'search' && searchQuery.length >= 2) {
      const timer = setTimeout(async () => {
        setSearching(true);
        try {
          const results = await searchOrganizations(searchQuery);
          setSearchResults(results);
        } catch (e) {
          console.error(e);
        } finally {
          setSearching(false);
        }
      }, 500);
      return () => clearTimeout(timer);
    } else {
      setSearchResults([]);
    }
  }, [searchQuery, joinMethod]);

  const handleJoin = async () => {
    if (inputCode.length < 2) {
      setError("Code is too short.");
      return;
    }
    setLoading(true);
    setError('');
    try {
      const org = await joinOrganization(userId, inputCode);
      if (org) {
        setSuccess(true);
        // onComplete(); // Wait for App listener to update and switch view
        return;
      } else {
        setError("Organization not found. Please check your code.");
      }
    } catch (e) {
      setError("Failed to join organization.");
    }
    setLoading(false);
  };

  const handleCreate = async () => {
    if (!orgName.trim()) {
      setError("Please enter a church name.");
      return;
    }
    setLoading(true);
    setError('');
    try {
      await createOrganization(userId, orgName);
      setSuccess(true);
      // onComplete(); // Wait for App listener to update and switch view
      return;
    } catch (e) {
      setError("Failed to create organization.");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <AppHeader
        user={user}
        organization={null}
        onLogout={onLogout}
        activeTab={activeTab}
        setActiveTab={handleTabChange}
      />
      <div className="flex-1 flex flex-col items-center justify-center p-6 animate-fade-in">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
        <div className="text-center mb-8">
          <div className="bg-primary/10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-primary">
            <Icons.User className="w-8 h-8" />
          </div>
          <h2 className="text-2xl font-serif font-bold text-gray-900">Welcome, {userName}!</h2>
          <p className="text-gray-500 mt-2">Let's get you connected to your church.</p>
        </div>

        {isAutoJoining || success || !!user.orgId ? (
          <div className="text-center">
            <Icons.Loader className="animate-spin w-8 h-8 text-primary mx-auto mb-4" />
            <p className="font-semibold text-gray-700">{success || user.orgId ? 'Preparing your dashboard...' : 'Joining organization...'}</p>
            <p className="text-sm text-gray-500">Please wait a moment.</p>
          </div>
        ) : (
          <>
            {error && (
              <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg mb-6 flex items-center gap-2">
                <Icons.X className="w-4 h-4" /> {error}
              </div>
            )}

            {mode === 'select' && (
              <div className="space-y-4">
                <button 
                  onClick={() => setMode('join')}
                  className="w-full p-6 border-2 border-gray-100 rounded-xl hover:border-primary hover:bg-blue-50 transition-all group text-left"
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-lg text-gray-800">Join a Church</span>
                    <Icons.Next className="w-5 h-5 text-gray-400 group-hover:text-primary" />
                  </div>
                  <p className="text-sm text-gray-500">I have a code from my leader.</p>
                </button>

                {isTeacher && (
                  <button 
                    onClick={() => setMode('create')}
                    className="w-full p-6 border-2 border-gray-100 rounded-xl hover:border-secondary hover:bg-yellow-50 transition-all group text-left"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-bold text-lg text-gray-800">Create New Ministry</span>
                      <Icons.Next className="w-5 h-5 text-gray-400 group-hover:text-secondary" />
                    </div>
                                      <p className="text-sm text-gray-500">I want to create studies for my group.</p>
                                    </button>
                                  )}
                                </div>
                              )}
            {mode === 'join' && (
              <div>
                {/* Toggle Tabs */}
                <div className="flex bg-gray-100 p-1 rounded-xl mb-6">
                  <button
                    onClick={() => { setJoinMethod('search'); setError(''); }}
                    className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${
                      joinMethod === 'search' ? 'bg-white shadow text-primary' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Search by Name
                  </button>
                  <button
                    onClick={() => { setJoinMethod('code'); setError(''); }}
                    className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all ${
                      joinMethod === 'code' ? 'bg-white shadow text-primary' : 'text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    Enter Code
                  </button>
                </div>

                {joinMethod === 'code' ? (
                  <>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Enter Invite Code</label>
                    <input 
                      type="text"
                      value={inputCode}
                      onChange={(e) => setInputCode(e.target.value.toUpperCase())}
                      placeholder="e.g. GCC or GRACE1"
                      className="w-full text-center text-3xl tracking-widest p-4 border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none uppercase font-mono mb-6"
                    />
                    <button 
                      onClick={handleJoin}
                      disabled={loading || inputCode.length < 2}
                      className="w-full py-3 bg-primary text-white font-bold rounded-lg hover:bg-blue-900 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {loading && <Icons.Loader className="animate-spin w-4 h-4" />}
                      Join Church
                    </button>
                  </>
                ) : (
                  <>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Find Your Church</label>
                    <div className="relative mb-4">
                      <Icons.Search className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" />
                      <input 
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="e.g. Grace Community"
                        className="w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-primary focus:outline-none"
                      />
                    </div>

                    <div className="space-y-2 mb-6 max-h-60 overflow-y-auto">
                      {searching ? (
                        <div className="text-center py-4 text-gray-500"><Icons.Loader className="animate-spin w-5 h-5 mx-auto" /></div>
                      ) : searchResults.length > 0 ? (
                        searchResults.map(org => (
                          <button
                            key={org.id}
                            onClick={() => {
                              setInputCode(org.code);
                              // Auto join using the code from the selected org
                              setLoading(true);
                              joinOrganization(userId, org.code)
                                .then(joinedOrg => {
                                  if (joinedOrg) {
                                    setSuccess(true);
                                    // onComplete(); // Wait for App listener
                                    return;
                                  }
                                  setError("Failed to join.");
                                  setLoading(false);
                                })
                                .catch(() => setLoading(false));
                            }}
                            className="w-full p-3 text-left border rounded-lg hover:bg-blue-50 hover:border-blue-200 transition-colors flex items-center justify-between group"
                          >
                            <span className="font-medium text-gray-800">{org.name}</span>
                            <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded group-hover:bg-blue-100 group-hover:text-blue-700">Select</span>
                          </button>
                        ))
                      ) : searchQuery.length > 0 && searchQuery.length < 2 ? (
                        <div className="text-center py-4 text-gray-400 text-sm italic">Keep typing to search...</div>
                      ) : searchQuery.length >= 2 ? (
                        <div className="text-center py-4 text-gray-500 text-sm">No churches found. Try a different name or ask for the code.</div>
                      ) : (
                        <div className="text-center py-4 text-gray-400 text-sm">Type your church's name to find it...</div>
                      )}
                    </div>
                  </>
                  )}

                {isTeacher && (
                  <button
                    onClick={() => { setMode('select'); setError(''); }}
                    className="w-full mt-4 text-sm text-gray-500 hover:text-gray-800"
                  >
                    Back
                  </button>
                )}
              </div>
            )}
            {mode === 'create' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ministry / Church Name</label>
                <input 
                  type="text"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  placeholder="e.g. Grace Community Church"
                  className="w-full p-3 border rounded-xl focus:ring-2 focus:ring-secondary focus:outline-none mb-6"
                />
                <button 
                  onClick={handleCreate}
                  disabled={loading || !orgName.trim()}
                  className="w-full py-3 bg-secondary text-blue-900 font-bold rounded-lg hover:bg-yellow-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {loading && <Icons.Loader className="animate-spin w-4 h-4" />}
                  Create & Become Admin
                </button>
                <button 
                  onClick={() => { setMode('select'); setError(''); }}
                  className="w-full mt-4 text-sm text-gray-500 hover:text-gray-800"
                >
                  Back
                </button>
              </div>
            )}
          </>
        )}
        </div>
      </div>
    </div>
  );
};